import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import defaultValidators from '@/helpers/default-validators';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import TextArea from '@/molecules/TextArea.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import { MODAL_CONTENT_NOVARTIS } from '@/parcours/flows/novartis/modalContentNovartis';
import { api, api_v1 } from '@/services/api';

const MONTH_NAMES = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

function formatBirthDate(patient: any): string {
  const { birth_day, birth_month, birth_year } = patient ?? {};
  if (!birth_day || !birth_month || !birth_year) return 'non précisé';
  return `${birth_day} ${MONTH_NAMES[Number(birth_month) - 1]} ${birth_year}`;
}

export const novartisFlow = withGlobalFlow({
  id: 'novartisFlow',

  basePath: '/novartisFlow/flow',

  exitRoute: '/novartisFlow',

  async onInit({ route }) {
    const slotId = route.params.slotId;

    const slotRes = await api.get(`/cms/slot/${slotId}`);
    const patientId = slotRes.data?.patients?.[0]?.id;

    const [patientRes, coordinatorRes] = await Promise.all([
      api_v1.get(`/pros/account/patient/id/${patientId}/slot/${slotId}?isFromLab=true`),
      api_v1.get(
        `/pros/account/patient/${patientId}/slot/${slotId}/coordinator-patient-novartis-infos`,
      ),
    ]);

    return {
      patient: patientRes.data,
      ...coordinatorRes.data,
    };
  },

  steps: {
    patientInfos: {
      type: 'component',
      component: ListInfo,
      title: 'Informations patient',
      props: (data: any) => {
        const patient = data?.flowData?.patient;
        const civility = patient?.civility === 'F' ? 'Madame' : 'Monsieur';
        const birthDate = formatBirthDate(patient);

        // Si nom de naissance : remplace Prénom (comportement Angular)
        const patientItems = patient?.birth_name
          ? [
              { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
              { label: 'Nom de naissance', value: patient.birth_name },
              { key: 'patient.civility', label: 'Civilité', value: civility },
              { label: 'Date de naissance', value: birthDate },
              { key: 'patient.email', label: 'Adresse e-mail', source: 'flowData' },
              { key: 'patient.phone', label: 'N° de téléphone', source: 'flowData' },
            ]
          : [
              { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
              { key: 'patient.firstname', label: 'Prénom', source: 'flowData' },
              { key: 'patient.civility', label: 'Civilité', value: civility },
              { label: 'Date de naissance', value: birthDate },
              { key: 'patient.email', label: 'Adresse e-mail', source: 'flowData' },
              { key: 'patient.phone', label: 'N° de téléphone', source: 'flowData' },
            ];

        return {
          data,
          config: [
            {
              section: 'Coordonnées du patient',
              items: patientItems,
            },
          ],
        };
      },
    },

    injectionDates: {
      type: 'component',
      component: ListInfo,
      title: "Programme d'apprentissage patient",
      props: (data: any) => ({
        data,
        config: [
          {
            section: 'Informations patient',
            items: [
              {
                // Valeurs déjà formatées côté API (DD/MM/YYYY) — et parfois du
                key: 'previousInjectionDate',
                label: 'Date de la dernière injection du patient',
                source: 'flowData',
              },
              {
                key: 'nextInjectionDate',
                label: 'Date de la prochaine injection du patient',
                source: 'flowData',
              },
            ],
          },
        ],
        actions: [
          {
            label: 'Remontées de pharmacovigilance',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Remontées de pharmacovigilance',
                content: MODAL_CONTENT_NOVARTIS.PHARMACOVIGILANCE,
              });
            },
          },
        ],
      }),
    },

    report: {
      type: 'form',
      title: 'Compte-rendu de visite',
      description: "En PratiK | Programme d'apprentissage patient",
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'understand_steps_treatment',
            type: 'radio-group',
            as: RadioRowItem,
            label:
              "Le patient a-t-il bien compris les étapes de son traitement et du programme d'apprentissage ?",
            options: [
              { label: 'Oui', value: true },
              { label: 'Non', value: false },
            ],
            optionValue: 'value',
            optionLabel: (option: any) => option.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'mastery_technical_gesture',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Le patient maîtrise-t-il le geste technique à réaliser ?',
            options: [
              { label: 'Oui', value: true },
              { label: 'Non', value: false },
            ],
            optionValue: 'value',
            optionLabel: (option: any) => option.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'idel_comment',
            type: 'textarea',
            as: TextArea,
            label: 'Autres commentaires et recommandations',
            placeholder: 'Écrire un commentaire...',
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    patientInfos: { next: 'injectionDates' },
    injectionDates: { next: 'report' },
    report: { submit: true },
  },

  async onSubmit(data) {
    const patientId = data?.patient?.id;
    const visitId = data?.id;
    await api_v1.put(`/pros/account/patient/${patientId}/visit/${visitId}`, {
      understand_steps_treatment: data?.understand_steps_treatment,
      mastery_technical_gesture: data?.mastery_technical_gesture,
      idel_comment: data?.idel_comment,
    });
  },
});
